import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, Eye, Mail, Save, Send, Users } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Announcements to registrants and members.
 *
 * Sending is deliberately slow — it shares a mailbox with registration
 * confirmations and receipts — so the screen shows the remaining daily
 * allowance before a send rather than after it fails.
 */

interface EventOption { _id: string; title: string }

interface Campaign {
  _id: string;
  subject: string;
  status: string;
  scheduledFor?: string;
  createdAt?: string;
  haltReason?: string;
  counts: { total: number; sent: number; failed: number; pending: number; skipped: number };
  audience?: { kind: string; event?: { title?: string }; role?: string };
}

interface Template { _id: string; name: string; subject: string; body: string }

interface Allowance { used: number; cap: number; remaining: number; batchSize: number; pauseMs: number }

type Section = 'compose' | 'history' | 'templates';

export default function EmailPanel({ events }: { events: EventOption[] }) {
  const { success, error } = useToast();
  const [section, setSection] = useState<Section>('compose');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'event' | 'role' | 'manual'>('event');
  const [eventId, setEventId] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState('all');
  const [payment, setPayment] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [role, setRole] = useState('all');
  const [manualEmails, setManualEmails] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [includeGroupInvite, setIncludeGroupInvite] = useState(false);

  const [preview, setPreview] = useState<{ total: number; optedOut: number; sample: { email: string; name: string }[] } | null>(null);
  const [allowance, setAllowance] = useState<Allowance | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [busy, setBusy] = useState(false);

  const audience = () => ({
    kind,
    ...(kind === 'event' && {
      event: eventId,
      registrationStatus,
      paidOnly: payment === 'paid',
      unpaidOnly: payment === 'unpaid'
    }),
    ...(kind === 'role' && { role }),
    ...(kind === 'manual' && {
      emails: manualEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)
    })
  });

  const loadAllowance = useCallback(async () => {
    try {
      const res = await api.get('/emails/allowance');
      setAllowance(res.data);
    } catch { /* shown by the send itself if it matters */ }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      const res = await api.get('/emails/campaigns');
      setCampaigns(res.data.campaigns || []);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not load past sends');
    }
  }, [error]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/emails/templates');
      setTemplates(res.data.templates || []);
    } catch { /* templates are optional */ }
  }, []);

  useEffect(() => { loadAllowance(); loadTemplates(); }, [loadAllowance, loadTemplates]);
  useEffect(() => { if (section === 'history') loadCampaigns(); }, [section, loadCampaigns]);

  const doPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post('/emails/preview', { audience: audience(), subject, body });
      setPreview(res.data);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not work out the recipients');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) { error('Write a subject and a message first'); return; }
    setBusy(true);
    try {
      const res = await api.post('/emails/test', { subject, body });
      success(res.data.message);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not send the test');
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!preview) { error('Check the recipients first'); return; }
    const when = scheduledFor ? new Date(scheduledFor) : null;
    const what = when
      ? `Schedule this for ${when.toLocaleString('en-IN')}, to ${preview.total} recipient${preview.total === 1 ? '' : 's'}?`
      : `Send now to ${preview.total} recipient${preview.total === 1 ? '' : 's'}?`;
    if (!window.confirm(`${what}\n\nThis cannot be recalled once it starts.`)) return;

    setBusy(true);
    try {
      await api.post('/emails/campaigns', {
        subject, body, audience: audience(), includeGroupInvite,
        ...(when ? { scheduledFor: when.toISOString() } : {})
      });
      success(when ? 'Scheduled' : 'Sending — this takes a few minutes');
      setSubject(''); setBody(''); setPreview(null); setScheduledFor('');
      loadAllowance();
      setSection('history');
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not start the send');
    } finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt('Save this as a template. What should it be called?');
    if (!name?.trim()) return;
    try {
      await api.post('/emails/templates', { name, subject, body });
      success('Template saved');
      loadTemplates();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not save the template');
    }
  };

  const resume = async (id: string) => {
    try {
      await api.post(`/emails/campaigns/${id}/resume`);
      success('Continuing the send');
      setTimeout(loadCampaigns, 1500);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not continue');
    }
  };

  const stop = async (id: string) => {
    if (!window.confirm('Stop this send? Anything already sent has gone.')) return;
    try {
      await api.post(`/emails/campaigns/${id}/pause`);
      success('Stopped');
      loadCampaigns();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not stop it');
    }
  };

  const tab = (id: Section, label: string) => (
    <button
      type="button"
      onClick={() => setSection(id)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium ${
        section === id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {tab('compose', 'Write')}
          {tab('history', 'Sent')}
          {tab('templates', 'Templates')}
        </div>
        {allowance && (
          <div className={`rounded-md border px-3 py-2 text-sm ${
            allowance.remaining < 50 ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-white text-gray-600'
          }`}>
            <strong>{allowance.remaining}</strong> of {allowance.cap} left to send today
          </div>
        )}
      </div>

      {section === 'compose' && (
        <div className="space-y-5">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
              <Users className="h-5 w-5 text-gray-500" /> Who it goes to
            </h3>

            <div className="mb-4 flex flex-wrap gap-4">
              {([['event', 'People registered for an event'], ['role', 'Members by role'], ['manual', 'Addresses I type']] as const).map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="radio" name="audience-kind" checked={kind === k}
                    onChange={() => { setKind(k); setPreview(null); }} className="h-4 w-4" />
                  {label}
                </label>
              ))}
            </div>

            {kind === 'event' && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label htmlFor="em-event" className="mb-1 block text-sm font-medium text-gray-700">Event</label>
                  <select id="em-event" value={eventId} onChange={(e) => { setEventId(e.target.value); setPreview(null); }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2">
                    <option value="">Choose an event…</option>
                    {events.map((e) => <option key={e._id} value={e._id}>{e.title}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="em-status" className="mb-1 block text-sm font-medium text-gray-700">Registration status</label>
                  <select id="em-status" value={registrationStatus} onChange={(e) => { setRegistrationStatus(e.target.value); setPreview(null); }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2">
                    <option value="all">Any</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="registered">Awaiting approval</option>
                    <option value="attended">Attended</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="em-paid" className="mb-1 block text-sm font-medium text-gray-700">Payment</label>
                  <select id="em-paid" value={payment} onChange={(e) => { setPayment(e.target.value as any); setPreview(null); }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2">
                    <option value="all">Either</option>
                    <option value="paid">Has paid</option>
                    <option value="unpaid">Has not paid</option>
                  </select>
                </div>
              </div>
            )}

            {kind === 'role' && (
              <div className="max-w-xs">
                <label htmlFor="em-role" className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                <select id="em-role" value={role} onChange={(e) => { setRole(e.target.value); setPreview(null); }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2">
                  <option value="all">Everyone with an account</option>
                  <option value="aspirant">Aspirants</option>
                  <option value="mentor">Mentors</option>
                </select>
              </div>
            )}

            {kind === 'manual' && (
              <div>
                <label htmlFor="em-manual" className="mb-1 block text-sm font-medium text-gray-700">Addresses</label>
                <textarea id="em-manual" rows={3} value={manualEmails}
                  onChange={(e) => { setManualEmails(e.target.value); setPreview(null); }}
                  placeholder="one per line, or separated by commas"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm" />
              </div>
            )}

            <button type="button" onClick={doPreview} disabled={busy}
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <Eye className="h-4 w-4" /> Check the recipients
            </button>

            {preview && (
              <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm text-blue-900">
                  <strong>{preview.total}</strong> recipient{preview.total === 1 ? '' : 's'}
                  {preview.optedOut > 0 && <> · {preview.optedOut} removed for having unsubscribed</>}
                </p>
                {preview.sample.length > 0 && (
                  <p className="mt-2 break-words text-xs text-blue-800">
                    {preview.sample.map((r) => r.email).join(', ')}
                    {preview.total > preview.sample.length && ` … and ${preview.total - preview.sample.length} more`}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
              <Mail className="h-5 w-5 text-gray-500" /> The message
            </h3>

            {templates.length > 0 && (
              <div className="mb-4">
                <label htmlFor="em-template" className="mb-1 block text-sm font-medium text-gray-700">Start from a template</label>
                <select id="em-template" defaultValue=""
                  onChange={(e) => {
                    const t = templates.find((x) => x._id === e.target.value);
                    if (t) { setSubject(t.subject); setBody(t.body); }
                  }}
                  className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2">
                  <option value="">Write from scratch</option>
                  {templates.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>
            )}

            <label htmlFor="em-subject" className="mb-1 block text-sm font-medium text-gray-700">Subject</label>
            <input id="em-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Joining instructions for {{name}}"
              className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2" />

            <label htmlFor="em-body" className="mb-1 block text-sm font-medium text-gray-700">Message</label>
            <textarea id="em-body" rows={10} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{name}},&#10;&#10;Basic HTML works — <b>bold</b>, <a href='…'>links</a>, <br/> for a new line."
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm" />
            <p className="mt-1 text-xs text-gray-500">
              <code>{'{{name}}'}</code> and <code>{'{{email}}'}</code> are replaced per recipient. A blank line
              starts a new paragraph and a single line break is kept, so the message arrives laid out
              as you typed it. An unsubscribe link is added to every message.
            </p>

            <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={includeGroupInvite}
                onChange={(e) => setIncludeGroupInvite(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span className="text-sm text-gray-800">
                <strong>Add the community group button</strong>
                <span className="mt-0.5 block text-xs text-gray-600">
                  The same green block registration confirmations carry. The link is taken from
                  Site Settings when the message is sent, so if a group invite is reset the current
                  one goes out — nothing to paste or keep up to date.
                </span>
              </span>
            </label>

            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="em-when" className="mb-1 block text-sm font-medium text-gray-700">Send at</label>
                <input id="em-when" type="datetime-local" value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2" />
                <p className="mt-1 text-xs text-gray-500">Leave blank to send now</p>
              </div>

              <button type="button" onClick={sendTest} disabled={busy}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <Send className="h-4 w-4" /> Send one to me
              </button>

              <button type="button" onClick={saveTemplate} disabled={!subject.trim() || !body.trim()}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <Save className="h-4 w-4" /> Save as template
              </button>

              <button type="button" onClick={send} disabled={busy || !preview || !subject.trim() || !body.trim()}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {scheduledFor ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                {scheduledFor ? 'Schedule' : 'Send'}
                {preview && ` to ${preview.total}`}
              </button>
            </div>

            {!preview && (subject.trim() || body.trim()) && (
              <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Check the recipients before sending.
              </p>
            )}
          </div>
        </div>
      )}

      {section === 'history' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-semibold">Subject</th>
                <th className="px-4 py-3 font-semibold">Audience</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Progress</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nothing sent yet.</td></tr>
              )}
              {campaigns.map((c) => (
                <tr key={c._id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-gray-900">{c.subject}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.audience?.event?.title || (c.audience?.kind === 'role' ? `Members: ${c.audience.role}` : 'Typed addresses')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      c.status === 'sent' ? 'bg-green-100 text-green-800'
                      : c.status === 'sending' ? 'bg-blue-100 text-blue-800'
                      : c.status === 'scheduled' ? 'bg-purple-100 text-purple-800'
                      : c.status === 'paused' ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-700'
                    }`}>{c.status}</span>
                    {c.haltReason && <div className="mt-1 max-w-xs text-xs text-amber-800">{c.haltReason}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {c.counts.sent} of {c.counts.total} sent
                    {c.counts.failed > 0 && <span className="text-red-600"> · {c.counts.failed} failed</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.scheduledFor && c.status === 'scheduled'
                      ? new Date(c.scheduledFor).toLocaleString('en-IN')
                      : c.createdAt ? new Date(c.createdAt).toLocaleString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status === 'paused' && (
                      <button type="button" onClick={() => resume(c._id)}
                        className="rounded-md border border-blue-300 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50">
                        Continue
                      </button>
                    )}
                    {(c.status === 'sending' || c.status === 'scheduled') && (
                      <button type="button" onClick={() => stop(c._id)}
                        className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50">
                        Stop
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section === 'templates' && (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          {templates.length === 0 ? (
            <p className="py-6 text-center text-gray-500">
              No templates yet. Write a message and use <strong>Save as template</strong>.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {templates.map((t) => (
                <li key={t._id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{t.name}</p>
                    <p className="truncate text-sm text-gray-500">{t.subject}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button type="button"
                      onClick={() => { setSubject(t.subject); setBody(t.body); setSection('compose'); }}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                      Use
                    </button>
                    <button type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete the template "${t.name}"?`)) return;
                        await api.delete(`/emails/templates/${t._id}`);
                        loadTemplates();
                      }}
                      className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50">
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
